import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import withRouter from "components/Common/withRouter";

import TableContainer from "components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import toast from "react-hot-toast";

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";

import Spinners from "components/Common/Spinner";
import { ListEvent, updateEventList, deleteEventList } from "../../../api";
import { handleError } from "utils/errorHandler";
import useCountryStore from "../../../store/countryStore";
import moment from "moment";

const EVENT_STATUS_OPTIONS = ["active", "inactive", "closed",];

const INITIAL_EDIT_FORM = {
  event_name: "",
  event_date: "",
  event_time: "",
  event_status: "active",
  organizer_name: "",
};

const EventListNew = () => {
  const navigate = useNavigate();

  document.title = "Event List | EventoX";

  const [eventList, setEventList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [event, setEvent] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);

  // Edit modal state
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [editLoading, setEditLoading] = useState(false);

  const { selectedCountryId } = useCountryStore();

  useEffect(() => {
    fetchAll();
    return () => {};
  }, [selectedCountryId]);

  const fetchAll = async () => {
    try {
      setIsLoading(true);
      const resp = await ListEvent();
      const data = Array.isArray(resp) ? resp : resp?.data || resp?.events || [];
      setEventList(data);
    } catch (error) {
      console.log(error);
      toast.error("Failed to load events");
    } finally {
      setIsLoading(false);
    }
  };

  const viewEvent = (eventData) => {
    navigate(`/event-detail/${eventData.event_id}`);
  };

  const handleEditEvent = (eventData) => {
    setEditForm({
      event_id: eventData.event_id,
      event_name: eventData.event_name || "",
      event_date: eventData.event_date
        ? moment(eventData.event_date).format("YYYY-MM-DD")
        : "",
      event_time: eventData.event_time || "",
      event_status: eventData.event_status || "active",
      organizer_name: eventData.organizer_name || "",
    });
    setEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!editForm.event_name?.trim()) {
      toast.error("Event name is required");
      return;
    }
    if (!editForm.event_date) {
      toast.error("Event date is required");
      return;
    }
    if (!editForm.event_status) {
      toast.error("Status is required");
      return;
    }

    try {
      setEditLoading(true);
      await updateEventList(editForm.event_id, {
        event_name: editForm.event_name,
        event_date: editForm.event_date,
        event_time: editForm.event_time,
        event_status: editForm.event_status,
        organizer_name: editForm.organizer_name,
      });
      toast.success("Event updated successfully");
      setEditModal(false);
      fetchAll();
    } catch (error) {
      toast.error("Error updating event: " + error?.message);
      handleError(error);
    } finally {
      setEditLoading(false);
    }
  };

  const onClickDelete = (eventData) => {
    setEvent(eventData);
    setDeleteModal(true);
  };

  const handleDeleteEvent = async () => {
    if (event && event.event_id) {
      try {
        await deleteEventList(event.event_id);
        toast.success("Event deleted successfully");
        fetchAll();
      } catch (error) {
        toast.error("Error deleting event: " + error?.message);
        handleError(error);
      }
    }
    setDeleteModal(false);
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "event_image",
        cell: (cell) => (
          <>
            {!cell.getValue() ? (
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle">
                  {cell.row.original.event_name?.charAt(0)}
                </span>
              </div>
            ) : (
              <div>
                <img
                  className="rounded-circle avatar-xs"
                  src={cell.getValue()}
                  alt=""
                />
              </div>
            )}
          </>
        ),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Event Name",
        accessorKey: "event_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => {
          return (
            <>
              <h5 className="font-size-14 mb-1">
                <Link to="#" className="text-dark">
                  {cell.getValue()}
                </Link>
              </h5>
              <p className="text-muted mb-0">
                {cell.row.original.event_location}
              </p>
              <p className="text-muted mb-0">
                {cell.row.original?.country?.countryName}
              </p>
              <p className="text-muted mb-0">
                {moment(cell.row.original.event_date).format("MMMM DD, YY")}
              </p>
            </>
          );
        },
      },
      {
        header: "Organizer",
        accessorKey: "organizer_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Category",
        accessorKey: "category",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => (
          <span>{cell.getValue()?.categoryName || "-"}</span>
        ),
      },
      {
        header: "Date",
        accessorKey: "event_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) =>
          cell.getValue()
            ? moment(cell.getValue()).format("MMMM DD, YYYY")
            : "-",
      },
      {
        header: "Status",
        accessorKey: "event_status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => {
          return (
            <div>
              <span className="badge badge-soft-primary font-size-11 m-1">
                {cell.getValue()}
              </span>
            </div>
          );
        },
      },
      {
        header: "Action",
        cell: (cellProps) => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18"></i>
              </DropdownToggle>

              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem
                  onClick={() => {
                    const customerData = cellProps.row.original;
                    handleEditEvent(customerData);
                  }}
                >
                  <i
                    className="mdi mdi-pencil font-size-16 text-success me-1"
                    id="edittooltip"
                  ></i>
                  Edit
                  <UncontrolledTooltip placement="top" target="edittooltip">
                    Edit
                  </UncontrolledTooltip>
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    const data = cellProps.row.original;
                    viewEvent(data);
                  }}
                >
                  <i
                    className="mdi mdi-eye font-size-16 text-success me-1"
                    id="viewtooltip"
                  ></i>
                  View Details
                  <UncontrolledTooltip placement="top" target="viewtooltip">
                    View Details
                  </UncontrolledTooltip>
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    const customerData = cellProps.row.original;
                    onClickDelete(customerData);
                  }}
                >
                  <i
                    className="mdi mdi-trash-can font-size-16 text-danger me-1"
                    id="deletetooltip"
                  ></i>
                  Delete
                  <UncontrolledTooltip placement="top" target="deletetooltip">
                    Delete
                  </UncontrolledTooltip>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  const handleClickNewEvent = () => {
    navigate("/create-list-event");
  };

  return (
    <React.Fragment>
      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteEvent}
        onCloseClick={() => setDeleteModal(false)}
      />

      {/* Edit Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} centered size="md">
        <ModalHeader toggle={() => setEditModal(false)}>Edit Event</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup className="mb-3">
              <Label for="event_name">
                Event Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="event_name"
                name="event_name"
                value={editForm.event_name}
                onChange={handleEditFormChange}
                placeholder="Enter event name"
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="event_date">
                    Event Date <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="date"
                    id="event_date"
                    name="event_date"
                    value={editForm.event_date}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="event_time">Event Time</Label>
                  <Input
                    type="time"
                    id="event_time"
                    name="event_time"
                    value={editForm.event_time}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-3">
              <Label for="event_status">
                Status <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="event_status"
                name="event_status"
                value={editForm.event_status}
                onChange={handleEditFormChange}
              >
                {EVENT_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="organizer_name">Organizer Name</Label>
              <Input
                type="text"
                id="organizer_name"
                name="organizer_name"
                value={editForm.organizer_name}
                onChange={handleEditFormChange}
                placeholder="Enter organizer name"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setEditModal(false)}>
            Cancel
          </Button>
          <Button
            color="success"
            onClick={handleEditSubmit}
            disabled={editLoading}
          >
            {editLoading ? "Saving..." : "Save Changes"}
          </Button>
        </ModalFooter>
      </Modal>

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Events List" />

          <Row>
            {isLoading ? (
              <Spinners setLoading={setIsLoading} />
            ) : (
              <Col lg="12">
                <Card>
                  <CardBody>
                    <Row>
                      <TableContainer
                        columns={columns}
                        data={eventList || []}
                        isGlobalFilter={true}
                        isPagination={true}
                        SearchPlaceholder="Search..."
                        isCustomPageSize={true}
                        isAddButton={true}
                        handleUserClick={handleClickNewEvent}
                        buttonClass="btn btn-success btn-rounded waves-effect waves-light addContact-modal mb-2"
                        buttonName="New Event"
                        tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                        theadClass="table-light"
                        paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                        pagination="pagination"
                      />
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(EventListNew);